// src/pages/TripPlanner/TripPlannerContext.tsx
import React, { createContext, useState, useContext } from 'react';

interface Destination {
  id: string;
  name: string;
  country: string;
  description: string;
  imageUrl: string;
  coordinates: [number, number]; // [longitude, latitude]
}

interface Accommodation {
  id: string;
  name: string;
  address: string;
  imageUrl: string;
  price: number;
  rating: number;
  coordinates: [number, number];
  url?: string;
}

interface Activity {
  id: string;
  title: string;
  description: string;
  date: Date;
  startTime?: string;
  endTime?: string;
  location: string;
  coordinates: [number, number];
  imageUrl?: string;
  url?: string;
  tags: string[];
}

interface Collaborator {
  id: string;
  name: string;
  email: string;
  profilePic?: string;
  status: 'owner' | 'invited' | 'joined';
}

interface TripPlan {
  id: string;
  title: string;
  startDate: Date | null;
  endDate: Date | null;
  destination: Destination | null;
  accommodations: Accommodation[];
  activities: Activity[];
  collaborators: Collaborator[];
  notes: string;
}

interface TripPlannerContextType {
  tripPlan: TripPlan;
  updateTripPlan: (updates: Partial<TripPlan>) => void;
  addActivity: (activity: Activity) => void;
  removeActivity: (activityId: string) => void;
  addAccommodation: (accommodation: Accommodation) => void;
  removeAccommodation: (accommodationId: string) => void;
  addCollaborator: (collaborator: Collaborator) => void;
  removeCollaborator: (collaboratorId: string) => void;
}

const defaultTripPlan: TripPlan = {
  id: '',
  title: 'New Trip',
  startDate: null,
  endDate: null,
  destination: null,
  accommodations: [],
  activities: [],
  collaborators: [],
  notes: ''
};

const TripPlannerContext = createContext<TripPlannerContextType | undefined>(undefined);

export const TripPlannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tripPlan, setTripPlan] = useState<TripPlan>(defaultTripPlan);

  const updateTripPlan = (updates: Partial<TripPlan>) => {
    setTripPlan(prev => ({ ...prev, ...updates }));
  };

  const addActivity = (activity: Activity) => {
    setTripPlan(prev => ({
      ...prev,
      activities: [...prev.activities, activity]
    }));
  };

  const removeActivity = (activityId: string) => {
    setTripPlan(prev => ({
      ...prev,
      activities: prev.activities.filter(a => a.id !== activityId)
    }));
  };

  const addAccommodation = (accommodation: Accommodation) => {
    setTripPlan(prev => ({
      ...prev,
      accommodations: [...prev.accommodations, accommodation]
    }));
  };

  const removeAccommodation = (accommodationId: string) => {
    setTripPlan(prev => ({
      ...prev,
      accommodations: prev.accommodations.filter(a => a.id !== accommodationId)
    }));
  };

  const addCollaborator = (collaborator: Collaborator) => {
    setTripPlan(prev => ({
      ...prev,
      collaborators: [...prev.collaborators, collaborator]
    }));
  };

  const removeCollaborator = (collaboratorId: string) => {
    setTripPlan(prev => ({
      ...prev,
      collaborators: prev.collaborators.filter(c => c.id !== collaboratorId)
    }));
  };

  return (
    <TripPlannerContext.Provider value={{
      tripPlan,
      updateTripPlan,
      addActivity,
      removeActivity,
      addAccommodation,
      removeAccommodation,
      addCollaborator,
      removeCollaborator
    }}>
      {children}
    </TripPlannerContext.Provider>
  );
};

export const useTripPlanner = () => {
  const context = useContext(TripPlannerContext);
  if (context === undefined) {
    throw new Error('useTripPlanner must be used within a TripPlannerProvider');
  }
  return context;
};